import React, { useMemo, useState } from "react";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, MapPin, Moon, X, ExternalLink } from "lucide-react";
import AlertModal from "../../../../components/AlertModal";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { SHIFT_COLORS } from "./shiftControlText";

const toMinutes = (hm) => {
  const [h, m] = String(hm || "").split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

export const isOvernight = (start, end) => {
  const s = toMinutes(start);
  const e = toMinutes(end);
  return s !== null && e !== null && e < s;
};

const mapLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

const emptyForm = (company) => ({
  name: "", workingCompany: company || "", color: SHIFT_COLORS[0],
  startTime: "09:00", endTime: "17:00",
  locationName: "", mapUrl: "", latitude: "", longitude: "", radius: 500,
});

function ShiftModal({ styles, t, theme, language, meta, initial, onClose, onSave }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState(() =>
    editing
      ? {
        name: initial.name, workingCompany: initial.workingCompany, color: initial.color || SHIFT_COLORS[0],
        startTime: initial.startTime, endTime: initial.endTime,
        locationName: initial.locationName, mapUrl: "",
        latitude: String(initial.latitude), longitude: String(initial.longitude), radius: initial.radius,
      }
      : emptyForm(initial?.workingCompany || meta.companies[0]),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedPickKey, setSavedPickKey] = useState(0); // resets the "pick saved" menu after each pick
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saved = meta.savedLocations?.[form.workingCompany] || [];
  const pickSaved = (e) => {
    const loc = saved[Number(e.target.value)];
    if (!loc) return;
    setForm((f) => ({
      ...f,
      locationName: loc.name || f.locationName,
      latitude: String(loc.latitude), longitude: String(loc.longitude),
      radius: Math.round(loc.radius), mapUrl: "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const hasCoords = form.mapUrl.trim() || (form.latitude !== "" && form.longitude !== "");
    if (!form.name.trim() || !form.workingCompany || !form.startTime || !form.endTime ||
      !form.locationName.trim() || !hasCoords) {
      setError(t.required);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(), color: form.color,
        startTime: form.startTime, endTime: form.endTime,
        locationName: form.locationName.trim(), radius: Number(form.radius),
        ...(form.mapUrl.trim()
          ? { mapUrl: form.mapUrl.trim() }
          : { latitude: Number(form.latitude), longitude: Number(form.longitude) }),
        ...(!editing && { workingCompany: form.workingCompany }),
      };
      await onSave(payload, initial?.id);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const night = isOvernight(form.startTime, form.endTime);

  return (
    <div style={styles.overlay} onClick={onClose} data-shift-form="true">
      <form className="sc-noscroll" style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div style={styles.modalTitle}>
          <span>{editing ? t.editShift : t.newShift}</span>
          <button type="button" className="sc-close" style={styles.closeBtn} onClick={onClose} aria-label={t.cancel}><X size={20} /></button>
        </div>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t.name}</label>
            <input className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.name} onChange={set("name")} maxLength={60} autoFocus />
          </div>
          <div>
            <label style={styles.label}>{t.company}</label>
            <ReusableDropdown
              items={meta.companies.map((c) => ({ key: c, label: c }))}
              value={form.workingCompany}
              onSelect={(item) => set("workingCompany")({ target: { value: item.key } })}
              placeholder={t.company}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              searchable={false}
              disabled={editing}
              maxItems={100}
              theme={theme}
              language={language}
              ariaLabel={t.company}
            />
          </div>
          <div>
            <label style={styles.label}>{t.startTime}</label>
            <input type="time" className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.startTime} onChange={set("startTime")} />
          </div>
          <div>
            <label style={styles.label}>{t.endTime}</label>
            <input type="time" className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.endTime} onChange={set("endTime")} />
          </div>
        </div>
        {night && (
          <div style={{ ...styles.hint, marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
            <Moon size={14} /> {t.overnightHint}
          </div>
        )}

        <label style={styles.label}>{t.color}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {SHIFT_COLORS.map((c) => (
            <button type="button" key={c} onClick={() => setForm((f) => ({ ...f, color: c }))}
              aria-label={c}
              style={{
                width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer",
                border: form.color === c ? `3px solid ${styles.dark ? "#1e293b" : "#fff"}` : "2px solid transparent",
                boxShadow: form.color === c ? `0 0 0 2px ${c}` : "none",
              }} />
          ))}
          <input type="color" value={form.color} onChange={set("color")}
            style={{ width: 36, height: 30, border: "none", background: "transparent", cursor: "pointer" }} />
        </div>

        <label style={{ ...styles.label, marginTop: 20, display: "flex", gap: 6, alignItems: "center" }}>
          <MapPin size={15} /> {t.location}
        </label>
        {saved.length > 0 ? (
          <ReusableDropdown
            key={`saved-${savedPickKey}`}
            items={saved.map((loc, i) => ({ key: String(i), label: loc.name || `${loc.latitude}, ${loc.longitude}` }))}
            value=""
            onSelect={(item) => { pickSaved({ target: { value: item.key } }); setSavedPickKey((n) => n + 1); }}
            placeholder={t.pickSaved}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            searchable={false}
            maxItems={100}
            theme={theme}
            language={language}
            ariaLabel={t.pickSaved}
          />
        ) : (
          <div style={styles.hint}>{t.noSaved}</div>
        )}

        <label style={styles.label}>{t.locationName}</label>
        <input className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.locationName} onChange={set("locationName")} />

        <label style={styles.label}>{t.mapUrl}</label>
        <input className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.mapUrl} onChange={set("mapUrl")}
          placeholder="https://maps.app.goo.gl/…" dir="ltr" />
        <div style={{ ...styles.hint, marginTop: 4 }}>{t.mapUrlHint}</div>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t.latitude}</label>
            <input className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.latitude} onChange={set("latitude")}
              disabled={Boolean(form.mapUrl.trim())} inputMode="decimal" dir="ltr" />
          </div>
          <div>
            <label style={styles.label}>{t.longitude}</label>
            <input className="sc-input" style={{ ...styles.input, width: "100%" }} value={form.longitude} onChange={set("longitude")}
              disabled={Boolean(form.mapUrl.trim())} inputMode="decimal" dir="ltr" />
          </div>
          <div>
            <label style={styles.label}>{t.radius}</label>
            <input type="number" min={10} max={10000} className="sc-input" style={{ ...styles.input, width: "100%" }}
              value={form.radius} onChange={set("radius")} />
          </div>
        </div>

        {error && <div style={{ color: "#ef4444", fontSize: 13, marginTop: 14 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22, flexWrap: styles.isMobile ? "nowrap" : "wrap" }}>
          <button type="button" className="sc-ghost" style={{ ...styles.btn("ghost"), ...(styles.isMobile && { flex: "1 1 0" }) }} onClick={onClose}>{t.cancel}</button>
          <button type="submit" className="sc-primary" style={{ ...styles.btn("primary", busy), ...(styles.isMobile && { flex: "1 1 0" }) }} disabled={busy}>{t.save}</button>
        </div>
      </form>
    </div>
  );
}

export default function ShiftsTab({ styles, t, theme, language, can, meta, shifts, loading, saveShift, setShiftActive, deleteShift, notifyError }) {
  const [company, setCompany] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null); // null | {} (new) | shift (edit)
  const [confirm, setConfirm] = useState(null); // {kind:'delete'|'deactivate', shift}

  const list = useMemo(
    () => shifts.filter((s) => (!company || s.workingCompany === company) && (showInactive || s.isActive)),
    [shifts, company, showInactive],
  );

  const runConfirm = async () => {
    const { kind, shift } = confirm;
    setConfirm(null);
    try {
      if (kind === "delete") await deleteShift(shift.id);
      else await setShiftActive(shift.id, false);
    } catch (err) {
      notifyError(err);
    }
  };

  const hoursLabel = (s) => (
    <span dir="ltr" style={{ whiteSpace: "nowrap" }}>
      {s.startTime} → {s.endTime} {s.isOvernight ? t.nextDay : ""}
    </span>
  );

  const actions = (s) => can.manage && (
    <div style={{ display: "flex", gap: 2 }}>
      <button style={styles.iconBtn()} title={t.edit} onClick={() => setModal(s)}><Pencil size={17} /></button>
      {s.isActive ? (
        <button style={styles.iconBtn("#f59e0b")} title={t.deactivate}
          onClick={() => setConfirm({ kind: "deactivate", shift: s })}><Ban size={17} /></button>
      ) : (
        <button style={styles.iconBtn("#22c55e")} title={t.activate}
          onClick={() => setShiftActive(s.id, true).catch(notifyError)}><CheckCircle2 size={17} /></button>
      )}
      {s.usageCount === 0 && (
        <button style={styles.iconBtn("#ef4444")} title={t.delete}
          onClick={() => setConfirm({ kind: "delete", shift: s })}><Trash2 size={17} /></button>
      )}
    </div>
  );

  return (
    <div style={styles.panel}>
      <AlertModal
        open={Boolean(confirm)}
        title={confirm?.kind === "delete" ? t.confirmDeleteTitle : t.confirmDeactivateTitle}
        message={confirm?.kind === "delete" ? t.confirmDelete : t.confirmDeactivate}
        variant="warning"
        showCancel
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
        theme={theme}
        language={language}
      />
      {modal && (
        <ShiftModal styles={styles} t={t} theme={theme} language={language} meta={meta} initial={modal.id ? modal : { workingCompany: company }}
          onClose={() => setModal(null)} onSave={saveShift} />
      )}

      <div style={{ ...styles.toolbar, flexShrink: 0 }}>
        <div style={styles.isMobile ? { flex: "1 1 100%", minWidth: 0 } : { flex: "0 1 240px", minWidth: 170 }}>
          <ReusableDropdown
            items={[{ key: "", label: t.allCompanies }, ...meta.companies.map((c) => ({ key: c, label: c }))]}
            value={company}
            onSelect={(item) => setCompany(item.key)}
            placeholder={t.allCompanies}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            searchable={false}
            maxItems={100}
            theme={theme}
            language={language}
            ariaLabel={t.allCompanies}
          />
        </div>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
          <input type="checkbox" className="sc-check" style={styles.checkbox} checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t.showInactive}
        </label>
        <div style={{ flex: 1 }} />
        {can.manage && (
          <button className="sc-primary" style={{ ...styles.btn("primary", !meta.companies.length), ...(styles.isMobile && { flex: "1 1 100%" }) }} disabled={!meta.companies.length}
            onClick={() => setModal({})}>
            <Plus size={16} /> {t.newShift}
          </button>
        )}
      </div>

      {loading && !shifts.length ? (
        <div style={styles.emptyState}>{t.loading}</div>
      ) : !list.length ? (
        <div style={styles.emptyState}>{t.noShifts}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))", gap: 16, alignContent: "start" }}>
          {list.map((s) => (
            <div key={s.id} style={{
              ...styles.card, padding: 18, opacity: s.isActive ? 1 : 0.6,
              borderTop: `3px solid ${s.color || "#3B82F6"}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={styles.dot(s.color || "#3B82F6")} />
                    {s.name}
                    {s.isOvernight && <span style={styles.badge("#8B5CF6")}><Moon size={11} />{t.overnight}</span>}
                  </div>
                  <div style={{ ...styles.hint, marginTop: 2 }}>{s.workingCompany}</div>
                </div>
                {actions(s)}
              </div>
              <div style={{ marginTop: 12, fontSize: 14, display: "grid", gap: 6 }}>
                <div>{hoursLabel(s)} · {s.lengthHours}{t.hoursShort}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                  <MapPin size={14} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.locationName} · {s.radius}m
                  </span>
                  <a href={mapLink(s.latitude, s.longitude)} target="_blank" rel="noopener noreferrer"
                    title={t.openMap} style={{ color: styles.accent, display: "inline-flex" }}>
                    <ExternalLink size={14} />
                  </a>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={styles.statusChip(s.isActive)}>{s.isActive ? t.active : t.inactive}</span>
                  <span style={styles.hint}>{t.usage}: {s.usageCount} {t.days}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
